import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error.js';

const FAVORITES_QUERY_KEY = ['workflow-favorites'] as const;

type FavoritesResponse = { ok: boolean; data: string[] };

/** Any cached /workflows list response whose items carry id + isFavorite. */
type CachedWorkflowList = { data?: Array<{ id: string; isFavorite?: boolean }> } | undefined;

/**
 * The viewer's starred workflows + an optimistic toggle.
 *
 * Toggling immediately flips the id in the favorites set AND patches
 * `isFavorite` on every cached workflows list (paginated + grouped) so stars
 * update everywhere at once; rolls back on error and reconciles on settle.
 */
export function useFlowFavorites() {
  const queryClient = useQueryClient();

  const favoritesQuery = useQuery<FavoritesResponse>({
    queryKey: FAVORITES_QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/me/workflow-favorites`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load favorites');
      return res.json();
    },
  });

  const favoriteIds = React.useMemo(
    () => new Set(favoritesQuery.data?.data ?? []),
    [favoritesQuery.data?.data]
  );

  const patchWorkflowCaches = React.useCallback(
    (workflowId: string, isFavorite: boolean) => {
      queryClient.setQueriesData<CachedWorkflowList>({ queryKey: ['workflows'] }, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old;
        let touched = false;
        const data = old.data.map((item) => {
          if (item.id !== workflowId || item.isFavorite === isFavorite) return item;
          touched = true;
          return { ...item, isFavorite };
        });
        return touched ? { ...old, data } : old;
      });
    },
    [queryClient]
  );

  const toggleMutation = useMutation<
    unknown,
    Error,
    { workflowId: string; next: boolean },
    { previousIds: string[] | undefined }
  >({
    mutationFn: async ({ workflowId, next }) => {
      const res = await fetch(`${API_URL}/me/workflow-favorites/${workflowId}`, {
        method: next ? 'POST' : 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update favorite');
      return res.json();
    },
    onMutate: async ({ workflowId, next }) => {
      await queryClient.cancelQueries({ queryKey: FAVORITES_QUERY_KEY });
      const previous = queryClient.getQueryData<FavoritesResponse>(FAVORITES_QUERY_KEY);

      queryClient.setQueryData<FavoritesResponse>(FAVORITES_QUERY_KEY, (old) => {
        const ids = new Set(old?.data ?? []);
        if (next) {
          ids.add(workflowId);
        } else {
          ids.delete(workflowId);
        }
        return { ok: true, data: Array.from(ids) };
      });
      patchWorkflowCaches(workflowId, next);

      return { previousIds: previous?.data };
    },
    onError: (_error, { workflowId, next }, context) => {
      // Roll back both the favorites set and the patched list entries
      queryClient.setQueryData<FavoritesResponse>(FAVORITES_QUERY_KEY, {
        ok: true,
        data: context?.previousIds ?? [],
      });
      patchWorkflowCaches(workflowId, !next);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const toggleFavorite = React.useCallback(
    (workflowId: string, next: boolean) => {
      toggleMutation.mutate({ workflowId, next });
    },
    [toggleMutation]
  );

  return {
    favoriteIds,
    isLoading: favoritesQuery.isLoading,
    toggleFavorite,
  };
}
